import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import RoadmapItemEditor from './RoadmapItemEditor';
import { VALID_ROADMAP_STATUS } from '../utils/chartUtils';

function RoadmapQuickAdd({ 
  onItemAdded, 
  existingItems = [], 
  onEditItem, 
  onRemoveItem, 
  showItemsList = true,
  onSave,
  isSaving,
  currentRoadmapId,
  hasUnsavedChanges,
  currentRoadmapName,
  compactMode = false // Nova prop para layout compacto
}) {
  // Removido o hook useSaveWithLogin - será gerenciado pelo App.js
  const [showForm, setShowForm] = useState(false);
  const [isItemsListVisible, setIsItemsListVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isAddingSubitem, setIsAddingSubitem] = useState(false);
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);
  const [newItem, setNewItem] = useState({
    Nome: '',
    Status: VALID_ROADMAP_STATUS[0], // 'Planejado'
    'Data de Inicio': '',
    'Previsão de Entrega': '',
    parentId: null,
    level: 0
  });

  const statusOptions = VALID_ROADMAP_STATUS;

  // Função para contar itens principais e subitens
  const getItemCounts = () => {
    const mainItems = existingItems.filter(item => {
      const itemLevel = item.level ?? 0;
      return itemLevel === 0 || !item.parentId;
    });
    const subItems = existingItems.filter(item => {
      const itemLevel = item.level ?? 0;
      return itemLevel > 0 && item.parentId;
    });
    return { mainItems: mainItems.length, subItems: subItems.length };
  };

  // Função para gerar texto descritivo dos itens
  const getItemsDescription = () => {
    const { mainItems, subItems } = getItemCounts();
    const parts = [];
    
    if (mainItems > 0) {
      parts.push(`${mainItems} ${mainItems === 1 ? 'item' : 'itens'}`);
    }
    
    if (subItems > 0) {
      parts.push(`${subItems} ${subItems === 1 ? 'subitem' : 'subitens'}`);
    }
    
    return parts.length > 0 ? parts.join(' e ') : '0 itens';
  };

  // Itens principais para seleção como pais (normaliza dados legados)
  const parentItems = existingItems.filter(item => {
    // Se não tem level definido, considera como item principal (level 0)
    const itemLevel = item.level ?? 0;
    // Se não tem parentId ou level é 0, é item principal
    const isParentItem = itemLevel === 0 || !item.parentId;
    return isParentItem;
  });

  const handleAddItem = (itemFromEditor) => {
    // 🔧 MELHORIA: Geração de ID único mais robusta
    const generateUniqueId = () => {
      return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    };

    const itemWithConclusao = { 
      ...itemFromEditor, 
      id: generateUniqueId(), // ID único e legível
      parentId: itemFromEditor.parentId || null,
      level: itemFromEditor.level ?? 0
    };
    
    // 🐛 DEBUG: Log do item sendo adicionado
    if (process.env.NODE_ENV === 'development') {
      console.log('🆕 [DEBUG QUICK ADD] Adicionando item:', {
        nome: itemWithConclusao.Nome,
        id: itemWithConclusao.id,
        parentId: itemWithConclusao.parentId,
        level: itemWithConclusao.level,
        isSubitem: itemWithConclusao.level > 0,
        item: itemWithConclusao
      });
    }
    
    // Chama callback antes de resetar estado
    onItemAdded(itemWithConclusao);
    
    // Reset do estado
    setNewItem({
      Nome: '',
      Status: VALID_ROADMAP_STATUS[0], // 'Planejado'
      'Data de Inicio': '',
      'Previsão de Entrega': '',
      parentId: null,
      level: 0
    });
    setShowForm(false);
    setIsAddingSubitem(false);
  };

  const handleEditItem = (updatedItem) => {
    onEditItem(updatedItem);
    setEditingId(null);
  };

  // Funções para modal de confirmação de remoção
  const handleRemoveClick = (item) => {
    setItemToRemove(item);
    setShowRemoveConfirmModal(true);
  };

  const confirmRemove = () => {
    if (itemToRemove && onRemoveItem) {
      onRemoveItem(itemToRemove.id);
    }
    setShowRemoveConfirmModal(false);
    setItemToRemove(null);
  };

  const cancelRemove = () => {
    setShowRemoveConfirmModal(false);
    setItemToRemove(null);
  };

  const handleInputChange = (field, value) => {
    setNewItem(prev => ({ ...prev, [field]: value }));
  };

  const handleCancel = () => {
    setShowForm(false);
    setIsAddingSubitem(false);
    setNewItem({
      Nome: '',
      Status: VALID_ROADMAP_STATUS[0], // 'Planejado'
      'Data de Inicio': '',
      'Previsão de Entrega': '',
      parentId: null,
      level: 0
    });
  };

    const handleShowItemForm = () => {
    setIsAddingSubitem(false);
    setNewItem({
      Nome: '',
      'Data de Inicio': '',
      'Previsão de Entrega': '',
      'Data de Conclusão': '',
      Status: 'Planejado',
      Responsavel: '',
      Progresso: 0,
      Prioridade: 'Média',
      Notas: '',
      level: 0
    });
    setShowForm(true);
  };

  const handleShowSubitemForm = () => {
    setIsAddingSubitem(true);
    setNewItem({
      Nome: '',
      Status: VALID_ROADMAP_STATUS[0], // 'Planejado'
      'Data de Inicio': '',
      'Previsão de Entrega': '',
      parentId: null, // Deixa nulo para que o usuário possa escolher
      level: 1
    });
    setShowForm(true);
  };

  return (
    <>
      {/* Modal renderizado diretamente no body usando portal quando em modo compacto */}
      {showForm && compactMode && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" 
          style={{ 
            zIndex: 99999,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)'
          }}
          onClick={(e) => {
            // Fechar modal se clicar no backdrop
            if (e.target === e.currentTarget) {
              handleCancel();
            }
          }}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden max-w-2xl w-full mx-auto"
            style={{
              maxHeight: '90vh',
              position: 'relative',
              zIndex: 100000
            }}
            onClick={(e) => e.stopPropagation()} // Evitar fechar quando clicar no modal
          >
            {/* Cabeçalho do modal */}
            <div className={`px-6 py-4 border-b ${isAddingSubitem ? 'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200' : 'bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm ${isAddingSubitem ? 'bg-amber-600' : 'bg-blue-600'}`}>
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      {isAddingSubitem ? (
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      )}
                    </svg>
                  </div>
                  <div>
                    <h4 className={`text-lg font-bold ${isAddingSubitem ? 'text-amber-900' : 'text-blue-900'}`}>
                      {isAddingSubitem ? 'Adicionar Subitem' : 'Adicionar Item'}
                    </h4>
                    <p className={`text-sm ${isAddingSubitem ? 'text-amber-700' : 'text-blue-700'}`}>
                      {isAddingSubitem ? 'Adicione um item filho ao roadmap' : 'Adicione um novo item ao roadmap'}
                    </p>
                  </div>
                </div>
                
                {/* Botão X para fechar */}
                <button
                  onClick={handleCancel}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full hover:bg-gray-100"
                  title="Fechar modal"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Conteúdo do modal */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
              <RoadmapItemEditor
                item={newItem}
                onSave={handleAddItem}
                onCancel={handleCancel}
                isEditing={false}
                parentItems={parentItems}
                isSubitem={isAddingSubitem}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
      
      <div className={showItemsList ? "w-full max-w-4xl mx-auto mt-6 space-y-4" : "w-full space-y-4"}>
      {/* Lista de itens existentes - só mostra se showItemsList for true */}
      {showItemsList && existingItems.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-lg font-semibold text-gray-700">
              Itens do Roadmap ({existingItems.length})
            </h4>
            <button
              onClick={() => setIsItemsListVisible(!isItemsListVisible)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              {isItemsListVisible ? 'Ocultar' : 'Mostrar'} itens
            </button>
          </div>
          
          {isItemsListVisible && (
            <div className="space-y-2">
              {existingItems.map(item => (
                <div key={item.id}>
                  {editingId === item.id ? (
                    <RoadmapItemEditor
                      item={item}
                      onSave={handleEditItem}
                      onCancel={() => setEditingId(null)}
                      isEditing={true}
                    />
                  ) : (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
                        <div>
                          <span className="font-medium text-gray-900">{item.Nome}</span>
                        </div>
                        <div>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            item.Status === 'Concluído' ? 'bg-green-100 text-green-800' :
                            item.Status === 'Em Progresso' ? 'bg-blue-100 text-blue-800' :
                            item.Status === 'Cancelado' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {item.Status}
                          </span>
                        </div>
                        <div className="text-gray-600">
                          {item['Data de Inicio'] ? `Início: ${item['Data de Inicio']}` : 'Sem data de início'}
                        </div>
                        <div className="text-gray-600">
                          {item['Previsão de Entrega'] ? `Previsão: ${item['Previsão de Entrega']}` : 'Sem previsão'}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => setEditingId(item.id)}
                          className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded"
                          title="Editar item"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleRemoveClick(item)}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                          title="Remover item"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Adicionar novo item */}
      {!showForm ? (
        <div className={compactMode ? "flex items-center gap-3" : "space-y-4"}>
          <div className={compactMode ? "flex items-center gap-3" : "flex flex-col sm:flex-row gap-3"}>
            <button
              onClick={handleShowItemForm}
              className={`${compactMode ? 'px-4 py-2 text-sm' : 'flex-1 py-2.5 px-4 text-sm'} bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center justify-center gap-2 font-medium shadow-md hover:shadow-lg transform hover:scale-105`}
            >
              <div className="w-4 h-4 bg-white bg-opacity-20 rounded-md flex items-center justify-center">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              {compactMode ? 'Novo Item' : 'Adicionar novo item'}
            </button>
            {parentItems.length > 0 && (
              <button
                onClick={handleShowSubitemForm}
                className={`${compactMode ? 'px-4 py-2 text-sm' : 'flex-1 py-2.5 px-4 text-sm'} bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-lg hover:from-amber-700 hover:to-amber-800 transition-all duration-200 flex items-center justify-center gap-2 font-medium shadow-md hover:shadow-lg transform hover:scale-105`}
              >
                <div className="w-4 h-4 bg-white bg-opacity-20 rounded-md flex items-center justify-center">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {compactMode ? 'Subitem' : 'Adicionar subitem'}
              </button>
            )}
          </div>
          
          {/* Botões de ação do roadmap */}
          {onSave && (
            <div className={compactMode ? "flex items-center gap-3 border-l border-gray-200 pl-3" : "border-t border-gray-200 pt-4"}>
              {/* Feedback visual do status de salvamento - versão compacta */}
              {compactMode ? (
                <div className="flex items-center gap-2">
                  {hasUnsavedChanges !== undefined && (
                    hasUnsavedChanges ? (
                      <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full flex items-center gap-1 font-medium">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        Não salvo
                      </span>
                    ) : currentRoadmapId && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full flex items-center gap-1 font-medium">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        Salvo
                      </span>
                    )
                  )}
                  
                  <button
                    onClick={onSave}
                    disabled={isSaving || existingItems.length === 0}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg hover:from-emerald-700 hover:to-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition-all shadow-md hover:shadow-lg transform hover:scale-105"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    {isSaving 
                      ? 'Salvando...' 
                      : currentRoadmapId 
                        ? 'Atualizar' 
                        : 'Salvar'
                    }
                  </button>
                </div>
              ) : (
                <>
                  {/* Versão original para modo não compacto */}
                  <div className="flex items-center justify-center mb-3">
                    {hasUnsavedChanges !== undefined ? (
                      <div className="flex items-center gap-2">
                        {hasUnsavedChanges ? (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-3 py-1.5 rounded-full flex items-center gap-2 font-medium">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            Roadmap não salvo • {getItemsDescription()}
                          </span>
                        ) : currentRoadmapId && (
                          <span className="text-xs bg-green-100 text-green-700 px-3 py-1.5 rounded-full flex items-center gap-2 font-medium">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            Roadmap salvo • {getItemsDescription()}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-600 text-sm font-medium">
                        Roadmap não salvo • {getItemsDescription()}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-center">
                    <button
                      onClick={onSave}
                      disabled={isSaving || existingItems.length === 0}
                      className="inline-flex items-center gap-3 px-8 py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl hover:from-emerald-700 hover:to-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      {isSaving 
                        ? 'Salvando...' 
                        : currentRoadmapId 
                          ? 'Atualizar Roadmap' 
                          : 'Salvar Roadmap'
                      }
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      ) : (
        /* Formulário quando showForm for true */
        showForm && !compactMode ? (
          /* Formulário inline para modo não compacto */
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className={`px-6 py-4 border-b ${isAddingSubitem ? 'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200' : 'bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm ${isAddingSubitem ? 'bg-amber-600' : 'bg-blue-600'}`}>
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    {isAddingSubitem ? (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    )}
                  </svg>
                </div>
                <div>
                  <h4 className={`text-lg font-bold ${isAddingSubitem ? 'text-amber-900' : 'text-blue-900'}`}>
                    {isAddingSubitem ? 'Adicionar Subitem' : 'Adicionar Item'}
                  </h4>
                  <p className={`text-sm ${isAddingSubitem ? 'text-amber-700' : 'text-blue-700'}`}>
                    {isAddingSubitem ? 'Adicione um item filho ao roadmap' : 'Adicione um novo item ao roadmap'}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <RoadmapItemEditor
                item={newItem}
                onSave={handleAddItem}
                onCancel={handleCancel}
                isEditing={false}
                parentItems={parentItems}
                isSubitem={isAddingSubitem}
              />
            </div>
          </div>
        ) : null
      )}

      {/* Modal de confirmação de remoção */}
      {showRemoveConfirmModal && itemToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 mx-4 max-w-md w-full">
            <div className="flex items-center mb-4">
              <span className="text-2xl mr-3">🗑️</span>
              <h3 className="text-lg font-semibold text-gray-900">Remover Item</h3>
            </div>
            
            <p className="text-gray-600 mb-2">
              Tem certeza que deseja remover <strong>"{itemToRemove.Nome || itemToRemove.nome || 'Item'}"</strong>?
            </p>
            
            {itemToRemove.level === 0 && (
              <p className="text-red-600 text-sm mb-4 bg-red-50 p-3 rounded-md border border-red-200">
                <span className="font-medium">⚠️ Atenção:</span> Este item principal será removido junto com todos os seus subitens.
              </p>
            )}
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={cancelRemove}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmRemove}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium transition-colors"
              >
                Remover
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

export default RoadmapQuickAdd;
